import { supabase } from '../lib/supabase'

// Video upload service with progress tracking and validation
export class VideoUploadService {
  constructor() {
    this.uploadTasks = new Map()
    this.maxFileSize = 500 * 1024 * 1024 // 500MB
    this.allowedFormats = [
      'video/mp4',
      'video/webm',
      'video/ogg',
      'video/avi',
      'video/mov',
      'video/wmv',
      'video/flv'
    ]
  }

  // Check if required storage buckets exist
  async checkStorageBuckets() {
    try {
      const { data: buckets, error } = await supabase.storage.listBuckets()
      
      if (error) {
        console.warn('Unable to check storage buckets:', error)
        return { hasLearningContent: false, hasThumbnails: false, error: error.message }
      }
      
      const bucketNames = buckets.map(bucket => bucket.name)
      const hasLearningContent = bucketNames.includes('learning-content')
      const hasThumbnails = bucketNames.includes('course-thumbnails')
      
      return { hasLearningContent, hasThumbnails, buckets: bucketNames }
    } catch (error) {
      console.warn('Error checking storage buckets:', error)
      return { hasLearningContent: false, hasThumbnails: false, error: error.message }
    }
  }

  // Validate video file before upload
  validateVideoFile(file) {
    const errors = []

    // Check file size
    if (file.size > this.maxFileSize) {
      errors.push(`File size exceeds ${this.maxFileSize / (1024 * 1024)}MB limit`)
    }

    // Check file type
    if (!this.allowedFormats.includes(file.type)) {
      errors.push(`Unsupported file format. Allowed formats: ${this.allowedFormats.join(', ')}`)
    }

    // Check file name
    if (!file.name || file.name.length > 255) {
      errors.push('Invalid file name')
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  // Generate unique file path
  generateFilePath(userId, fileName, contentType = 'videos') {
    const timestamp = Date.now()
    const randomId = Math.random().toString(36).substring(2, 15)
    const fileExtension = fileName.split('.').pop()
    const sanitizedName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_')
    
    return `${userId}/${contentType}/${new Date().getFullYear()}/${new Date().getMonth() + 1}/${timestamp}_${randomId}_${sanitizedName}`
  }

  // Extract video metadata
  async extractVideoMetadata(file) {
    return new Promise((resolve) => {
      const video = document.createElement('video')
      video.preload = 'metadata'
      let objectUrl = null
      
      const cleanup = () => {
        if (objectUrl) {
          try {
            URL.revokeObjectURL(objectUrl)
          } catch (error) {
            console.warn('Error revoking object URL:', error)
          }
          objectUrl = null
        }
      }
      
      video.onloadedmetadata = () => {
        const result = {
          duration: Math.round(video.duration),
          width: video.videoWidth,
          height: video.videoHeight,
          resolution: `${video.videoWidth}x${video.videoHeight}`,
          aspectRatio: video.videoWidth / video.videoHeight
        }
        // Clear the src before cleanup to prevent further access
        video.src = ''
        cleanup()
        resolve(result)
      }
      
      video.onerror = (error) => {
        console.warn('Video metadata extraction error:', error)
        const result = {
          duration: 0,
          width: 0,
          height: 0,
          resolution: 'unknown',
          aspectRatio: 1
        }
        // Clear the src before cleanup to prevent further access
        video.src = ''
        cleanup()
        resolve(result)
      }
      
      try {
        objectUrl = URL.createObjectURL(file)
        video.src = objectUrl
      } catch (error) {
        console.error('Error creating object URL:', error)
        resolve({
          duration: 0,
          width: 0,
          height: 0,
          resolution: 'unknown',
          aspectRatio: 1
        })
      }
    })
  }

  // Generate video thumbnail
  async generateThumbnail(file, timeOffset = 1) {
    return new Promise((resolve) => {
      const video = document.createElement('video')
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      let objectUrl = null
      
      const cleanup = () => {
        if (objectUrl) {
          try {
            URL.revokeObjectURL(objectUrl)
          } catch (error) {
            console.warn('Error revoking thumbnail object URL:', error)
          }
          objectUrl = null
        }
      }
      
      video.onloadedmetadata = () => {
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        video.currentTime = Math.min(timeOffset, video.duration / 2)
      }
      
      video.onseeked = () => {
        try {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
          canvas.toBlob((blob) => {
            // Clear the src before cleanup to prevent further access
            video.src = ''
            cleanup()
            resolve(blob)
          }, 'image/jpeg', 0.8)
        } catch (error) {
          console.error('Error generating thumbnail:', error)
          // Clear the src before cleanup to prevent further access
          video.src = ''
          cleanup()
          resolve(null)
        }
      }
      
      video.onerror = (error) => {
        console.warn('Video thumbnail generation error:', error)
        // Clear the src before cleanup to prevent further access
        video.src = ''
        cleanup()
        resolve(null)
      }
      
      try {
        objectUrl = URL.createObjectURL(file)
        video.src = objectUrl
      } catch (error) {
        console.error('Error creating object URL for thumbnail:', error)
        resolve(null)
      }
    })
  }

  // Upload video with progress tracking
  async uploadVideo(file, metadata, onProgress = () => {}) {
    let user, userId
    
    try {
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser()
      
      if (authError) {
        console.error('Authentication error:', authError)
        throw new Error(`Authentication failed: ${authError.message}. Please sign in and try again.`)
      }
      
      if (!authUser) {
        throw new Error('You must be signed in to upload videos. Please sign in and try again.')
      }
      
      // Verify user session is valid
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) {
        throw new Error('Your session has expired. Please sign in again to upload videos.')
      }
      
      user = authUser
      userId = user.id
      console.log('User authenticated for video upload:', userId)
    } catch (error) {
      if (error.message.includes('Authentication failed') || 
          error.message.includes('must be signed in') || 
          error.message.includes('session has expired')) {
        throw error
      }
      throw new Error('Unable to verify authentication. Please check your connection and try again.')
    }

    // Check if storage buckets exist (optional check - will proceed if check fails)
    const bucketCheck = await this.checkStorageBuckets()
    if (bucketCheck.error) {
      console.warn('Unable to verify storage buckets, proceeding with upload attempt:', bucketCheck.error)
    }

    // Validate file
    const validation = this.validateVideoFile(file)
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '))
    }
    const filePath = this.generateFilePath(userId, file.name)
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substring(2)}`

    try {
      // Extract video metadata
      const videoMetadata = await this.extractVideoMetadata(file)
      
      // Generate thumbnail
      const thumbnailBlob = await this.generateThumbnail(file)
      let thumbnailUrl = null
      
      if (thumbnailBlob) {
        const thumbnailPath = this.generateFilePath(userId, `${file.name}_thumb.jpg`, 'thumbnails')
        const { data: thumbnailData, error: thumbnailError } = await supabase.storage
          .from('course-thumbnails')
          .upload(thumbnailPath, thumbnailBlob)
        
        if (thumbnailError) {
          if (thumbnailError.message && thumbnailError.message.includes('Bucket not found')) {
            console.warn('Thumbnail bucket "course-thumbnails" not found. Skipping thumbnail upload. Video will be uploaded without thumbnail.')
          } else {
            console.warn('Thumbnail upload failed:', thumbnailError)
          }
        } else {
          const { data: { publicUrl } } = supabase.storage
            .from('course-thumbnails')
            .getPublicUrl(thumbnailPath)
          thumbnailUrl = publicUrl
        }
      }

      // Upload video file with progress tracking
      const { data, error } = await supabase.storage
        .from('learning-content')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
          onUploadProgress: (progress) => {
            const percentage = Math.round((progress.loaded / progress.total) * 100)
            onProgress({
              uploadId,
              percentage,
              loaded: progress.loaded,
              total: progress.total,
              status: 'uploading'
            })
          }
        })

      if (error) {
        if (error.message && error.message.includes('Bucket not found')) {
          throw new Error('Storage bucket not found. Please create the learning-content and course-thumbnails buckets in your Supabase project. Check the database/create_storage_buckets.sql file for setup instructions.')
        }
        throw error
      }

      // Get the uploaded file URL
      const { data: { publicUrl } } = supabase.storage
        .from('learning-content')
        .getPublicUrl(filePath)

      // Create video record in database
      const videoRecord = {
        title: metadata.title || file.name.replace(/\.[^/.]+$/, ''),
        description: metadata.description || '',
        video_url: publicUrl,
        thumbnail_url: thumbnailUrl,
        duration: videoMetadata.duration,
        file_size: file.size,
        video_format: file.type,
        resolution: videoMetadata.resolution,
        course_id: metadata.courseId || null,
        order_index: metadata.orderIndex || 0,
        is_preview: metadata.isPreview || false,
        created_by: userId
      }

      const { data: videoData, error: dbError } = await supabase
        .from('videos')
        .insert([videoRecord])
        .select()
        .single()

      if (dbError) {
        // Clean up uploaded file if database insert fails
        await supabase.storage.from('learning-content').remove([filePath])
        throw dbError
      }

      onProgress({
        uploadId,
        percentage: 100,
        status: 'completed',
        videoData
      })

      return {
        success: true,
        video: videoData,
        filePath,
        thumbnailUrl
      }

    } catch (error) {
      onProgress({
        uploadId,
        percentage: 0,
        status: 'error',
        error: error.message
      })
      throw error
    }
  }

  // Upload multiple videos
  async uploadMultipleVideos(files, metadata, onProgress = () => {}) {
    const results = []
    const totalFiles = files.length
    let completedFiles = 0

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const fileMetadata = {
        ...metadata,
        title: metadata.titles?.[i] || file.name.replace(/\.[^/.]+$/, ''),
        description: metadata.descriptions?.[i] || '',
        orderIndex: i
      }

      try {
        const result = await this.uploadVideo(file, fileMetadata, (progress) => {
          onProgress({
            ...progress,
            fileIndex: i,
            fileName: file.name,
            overallProgress: Math.round(((completedFiles + (progress.percentage / 100)) / totalFiles) * 100)
          })
        })

        results.push({ success: true, file: file.name, data: result })
        completedFiles++

      } catch (error) {
        results.push({ success: false, file: file.name, error: error.message })
        completedFiles++
      }

      onProgress({
        fileIndex: i,
        fileName: file.name,
        overallProgress: Math.round((completedFiles / totalFiles) * 100),
        status: completedFiles === totalFiles ? 'completed' : 'uploading'
      })
    }

    return results
  }

  // Cancel upload
  cancelUpload(uploadId) {
    if (this.uploadTasks.has(uploadId)) {
      const task = this.uploadTasks.get(uploadId)
      if (task.abort) {
        task.abort()
      }
      this.uploadTasks.delete(uploadId)
    }
  }

  // Get upload progress
  getUploadProgress(uploadId) {
    return this.uploadTasks.get(uploadId) || null
  }

  // Delete video
  async deleteVideo(videoId) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Get video record
    const { data: video, error: fetchError } = await supabase
      .from('videos')
      .select('*')
      .eq('id', videoId)
      .eq('created_by', user.id)
      .single()

    if (fetchError || !video) {
      throw new Error('Video not found or access denied')
    }

    // Extract file path from URL
    const url = new URL(video.video_url)
    const pathname = url.pathname
    
    // Extract the file path after 'learning-content/'
    const bucketIndex = pathname.indexOf('/learning-content/')
    if (bucketIndex === -1) {
      console.error('Invalid video URL format for deletion:', video.video_url)
      throw new Error('Invalid video URL format')
    }
    
    const filePath = pathname.substring(bucketIndex + '/learning-content/'.length)

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('learning-content')
      .remove([filePath])

    if (storageError) {
      console.warn('Failed to delete file from storage:', storageError)
    }

    // Delete thumbnail if exists
    if (video.thumbnail_url) {
      const thumbnailUrl = new URL(video.thumbnail_url)
      const thumbnailPathname = thumbnailUrl.pathname
      
      // Extract the file path after 'course-thumbnails/'
      const thumbnailBucketIndex = thumbnailPathname.indexOf('/course-thumbnails/')
      if (thumbnailBucketIndex !== -1) {
        const thumbnailPath = thumbnailPathname.substring(thumbnailBucketIndex + '/course-thumbnails/'.length)
        await supabase.storage.from('course-thumbnails').remove([thumbnailPath])
      }
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('videos')
      .delete()
      .eq('id', videoId)
      .eq('created_by', user.user.id)

    if (dbError) {
      throw dbError
    }

    return { success: true }
  }

  // Update video metadata
  async updateVideo(videoId, updates) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    const { data, error } = await supabase
      .from('videos')
      .update(updates)
      .eq('id', videoId)
      .eq('created_by', user.id)
      .select()
      .single()

    if (error) {
      throw error
    }

    return data
  }

  // Get video by ID
  async getVideo(videoId) {
    const { data, error } = await supabase
      .from('videos')
      .select('*')
      .eq('id', videoId)
      .single()

    if (error) {
      throw error
    }

    return data
  }

  // Get signed URL for private video content
  async getVideoSignedUrl(videoUrl, expiresIn = 3600) {
    try {
      // Check if user is authenticated
      const { data: { user }, error: authError } = await supabase.auth.getUser()
      if (authError || !user) {
        console.error('User not authenticated for video access:', authError)
        throw new Error('Authentication required to access video content')
      }

      // Extract file path from the stored URL
      const url = new URL(videoUrl)
      const pathname = url.pathname
      
      // Extract the file path after 'learning-content/'
      const bucketIndex = pathname.indexOf('/learning-content/')
      if (bucketIndex === -1) {
        console.error('Invalid video URL format:', videoUrl)
        throw new Error('Invalid video URL format')
      }
      
      const filePath = pathname.substring(bucketIndex + '/learning-content/'.length)
      
      // Log for debugging
      console.log('Generating signed URL for:', {
        filePath,
        userId: user.id,
        userEmail: user.email,
        expiresIn
      })
      
      const { data, error } = await supabase.storage
        .from('learning-content')
        .createSignedUrl(filePath, expiresIn)
      
      if (error) {
        console.error('Error creating signed URL:', error)
        throw new Error(`Failed to generate video access URL: ${error.message}`)
      }
      
      if (!data?.signedUrl) {
        throw new Error('No signed URL returned from storage service')
      }
      
      return data.signedUrl
    } catch (error) {
      console.error('Error generating signed URL:', error)
      throw error // Re-throw to allow proper error handling upstream
    }
  }

  // Get videos by course
  async getVideosByCourse(courseId) {
    const { data, error } = await supabase
      .from('videos')
      .select('*')
      .eq('course_id', courseId)
      .order('order_index', { ascending: true })

    if (error) {
      throw error
    }

    return data
  }

  // Get all learning videos (for viewing by all authenticated users)
  async getAllLearningVideos() {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      throw new Error('User not authenticated')
    }

    const { data, error } = await supabase
      .from('videos')
      .select(`
        *,
        courses (
          id,
          title,
          category
        )
      `)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching learning videos:', error)
      throw error
    }

    return data || []
  }

  // Get user's uploaded videos (for management purposes)
  async getUserVideos(userId = null) {
    const { data: { user } } = await supabase.auth.getUser()
    const targetUserId = userId || user?.id
    
    if (!targetUserId) {
      throw new Error('User not authenticated')
    }

    const { data, error } = await supabase
      .from('videos')
      .select(`
        *,
        courses (
          id,
          title,
          category
        )
      `)
      .eq('created_by', targetUserId)
      .order('created_at', { ascending: false })

    if (error) {
      throw error
    }

    return data
  }
}

// Create singleton instance
export const videoUploadService = new VideoUploadService()

// Utility functions
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

export const getVideoQuality = (resolution) => {
  const [width, height] = resolution.split('x').map(Number)
  
  if (height >= 2160) return '4K'
  if (height >= 1440) return '2K'
  if (height >= 1080) return 'Full HD'
  if (height >= 720) return 'HD'
  if (height >= 480) return 'SD'
  return 'Low Quality'
}